import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type BouquetBuilderItem, type StemCostEstimate } from "../../api/shopMode";
import type {
  BouquetTemplate,
  Color,
  ConsumablePreset,
  BouquetBuildPhoto,
  CustomerOrder,
  DefaultUom,
  ItemCategory,
  PaperRollRow,
  SubCategory,
} from "../../api/types";
import { BUILDER_TABS, PAPER_PRESETS, eur } from "../bouquet/builderShared";
import { amountOf, NO_AMOUNT } from "../../i18n/intl";
import { useT } from "../../i18n/LanguageProvider";
import { categoryLabel, colourLabel, flowerTypeLabel } from "../../i18n/taxonomy";
import { t as translate } from "../../i18n/t";
import type { PlainKey } from "../../i18n/types";
import { uomLabel } from "./shopFloorFormat";
import {
  bouquetCostFigure,
  builderCostEstimate,
  stemLineCostText,
  unitCostFigure,
  unknownCountPill,
} from "./kioskCostDisplay";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { ShopFloorInlineSell } from "./ShopFloorInlineSell";
import { useBoxPickPrompt } from "./useBoxPickPrompt";
import { ShopFloorOrderBrief } from "./ShopFloorOrderBrief";
import { parseRecipeChange, type RecipeChange } from "../../utils/recipeChangeConfirmation";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { StockItemFinder } from "./StockItemFinder";
import { useStockTicket } from "./useStockTicket";

interface Props {
  onExit(): void;
  /** Start directly in the ingredient builder with this template applied. */
  initialTemplate?: BouquetTemplate | null;
  shopifyTarget?: {
    orderId: string;
    lineId: string;
    templateId?: string | null;
    title?: string;
    onBuilt?: () => void;
  };
  orderTarget?: {
    order: CustomerOrder;
    onBuilt?: () => void;
  };
  /**
   * When set, the post-save success message offers a "Sell now" button that
   * hands the finalized build straight to the floor sale panel. Not shown for
   * Shopify builds (those are sold via the order's hand-off flow).
   */
  /** G-46 GAP 2: fired after the inline sale's confirmation finishes. */
  onSold?: () => void;
}

/** A flower/toy/etc. line: tap-to-add with a quantity. */
interface ItemLine {
  itemId: string;
  name: string;
  uom: DefaultUom;
  qty: number;
  /**
   * The next stem's cost from the builder feed; null when that lot has no
   * cost (WO-B, OD-198); undefined while the feed has said nothing about the
   * item (a template line before the catalogue loads, or not in stock here).
   */
  unitCost: number | null | undefined;
  unitSalePrice: number | null;
  categoryCode: string;
}

/** A paper/ribbon cut, resolved to a specific roll + length. */
interface PaperLine {
  uid: string;
  itemId: string;
  batchId: string;
  name: string;
  lengthLabel: string;
  sizePreset?: ConsumablePreset;
  customCm?: number;
}

function itemLinesFromTemplate(template: BouquetTemplate | null | undefined): ItemLine[] {
  if (!template) return [];
  return (template.lines ?? [])
    .filter((line) => (line.kind ?? "EXACT_ITEM") === "EXACT_ITEM" && line.itemId)
    .map((line) => ({
      itemId: line.itemId as string,
      name: line.item?.name ?? translate("common.item"),
      uom: line.uom,
      qty: Number(line.quantity) || 1,
      unitCost: undefined,
      unitSalePrice: null,
      categoryCode: line.item?.category?.code ?? "OTHER",
    }));
}

const PRESETS = PAPER_PRESETS as ReadonlyArray<{
  value: ConsumablePreset;
  labelKey: PlainKey;
  cm: number;
}>;

let paperUid = 0;

export function ShopFloorBouquetBuilder({
  onExit,
  initialTemplate,
  shopifyTarget,
  orderTarget,
  onSold,
}: Props) {
  const { t } = useT();

  // Catalog data.
  const [items, setItems] = useState<BouquetBuilderItem[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [rolls, setRolls] = useState<PaperRollRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Builder state.
  const [itemLines, setItemLines] = useState<ItemLine[]>(() =>
    itemLinesFromTemplate(initialTemplate),
  );
  const [paperLines, setPaperLines] = useState<PaperLine[]>([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState(initialTemplate?.id ?? "");
  const [name, setName] = useState(initialTemplate?.name ?? "");
  const [notes, setNotes] = useState("");
  const [orderSeeded, setOrderSeeded] = useState(false);

  // Filters.
  const [search, setSearch] = useState("");
  const [colorId, setColorId] = useState<string>(""); // "" = All
  const [subCategoryId, setSubCategoryId] = useState<string>(""); // "" = All types
  const [activeTab, setActiveTab] = useState<string>("FLOWER");

  // Roll size prompt.
  const [pendingRoll, setPendingRoll] = useState<PaperRollRow | null>(null);
  const [customCm, setCustomCm] = useState("40");

  /**
   * G-45: the FLOWER category's VAT rate and target margin, which set the
   * bouquet's floor. A bouquet is taxed at the FLOWER rate (G-30 single-supply:
   * the principal element is fresh flowers), so one category answers both.
   * Fetched from the taxonomy endpoint the floor plane already exposes.
   */
  const [bouquetPricingRule, setBouquetPricingRule] = useState<{
    vatRatePercent: number;
    targetMarginPercent: number;
  } | null>(null);

  /**
   * G-12 — photos of the finished bouquet. The backend has been complete since
   * 2026-07-28 with nothing able to reach it; this is the capture half.
   * Deliberately OPTIONAL (owner decision, G-12): a florist is never blocked on
   * taking a picture.
   */
  const [photos, setPhotos] = useState<BouquetBuildPhoto[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const libraryInputRef = useRef<HTMLInputElement | null>(null);

  // Save lifecycle.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // G-84: stems or items that stock says sit in a box — the server asks, we answer, we resend.
  const boxPrompt = useBoxPickPrompt();
  const [result, setResult] = useState<{
    cogs: string | null;
    /** WO-B (OD-198): components with no cost; `cogs` is then the known part. */
    unknownCostComponentCount: number | null;
    name: string | null;
    buildId: string | null;
    suggestedSalePriceEur: string | null;
  } | null>(null);

  /**
   * Checkout mode (owner layout call 2026-08-09): the moment a sellable build
   * exists, the flower catalog's job is done — the wide canvas hands itself
   * to the walk-in sale. Shop items appear there IMMEDIATELY (no "+ Add
   * item" tap), the bouquet panel keeps the money and the ticket. The stock
   * list still loads lazily — on entering checkout, cached across builds —
   * so the pure bouquet flow never waits on the catalogue fetch.
   *
   * OD-31 (owner 2026-08-09): an ORDER-linked build keeps the checkout
   * canvas and the photos, but gets NO sell controls — its bouquet is
   * handed over by collecting the order, never as a walk-in sale (the
   * server refuses that too).
   */
  const checkoutMode = result?.buildId != null;
  const walkInCheckout = checkoutMode && !orderTarget;
  // WO-B (OD-198): the saved build's cost — the known part, with the count of
  // components that have none.
  const resultCost = bouquetCostFigure({
    totalCogsEur: result?.cogs ?? null,
    unknownCostComponentCount: result?.unknownCostComponentCount ?? null,
  });

  // One shared implementation of the finder-side state (stock list, pick
  // rule, ticket) — the kiosk order form uses the same hook, so the cap
  // rule and the retry/invalidate semantics cannot drift between hosts.
  const sellTicket = useStockTicket(walkInCheckout);
  const { setLines: setSellLines, invalidateStock: invalidateSellStock } =
    sellTicket;

  /** Leave checkout: back to the flowers, ticket cleared, next customer. */
  const exitCheckout = useCallback(() => {
    setResult(null);
    setSellLines([]);
  }, [setSellLines]);

  const handleSold = useCallback(() => {
    setResult(null);
    setSellLines([]);
    // The sale just changed the very quantities the picker caps against —
    // the next checkout refetches instead of trusting the session cache.
    invalidateSellStock();
    onSold?.();
  }, [onSold, setSellLines, invalidateSellStock]);

  // ── Data loading ────────────────────────────────────────────────────────────
  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [itemRes, colorRes, subRes, rollRes, categoryRes] = await Promise.all([
        shopModeApi.floorListBouquetItems(),
        shopModeApi.floorListBouquetColors(),
        shopModeApi.floorListBouquetSubcategories().catch(() => [] as SubCategory[]),
        shopModeApi.floorListBouquetPaperRolls().catch(() => [] as PaperRollRow[]),
        // Guidance is a bonus, never a blocker: if this read fails the builder
        // still works, it just shows no floor.
        shopModeApi.floorListItemCategories().catch(() => [] as ItemCategory[]),
      ]);
      setItems(itemRes);
      setColors(colorRes);
      setSubcategories(subRes);
      setRolls(rollRes);
      const flower = categoryRes.find((c) => c.code === "FLOWER");
      setBouquetPricingRule(
        flower
          ? {
              vatRatePercent: Number(flower.vatRatePercent),
              targetMarginPercent: Number(flower.targetMarginPercent),
            }
          : null,
      );
    } catch (err: unknown) {
      setLoadError(errorText(err, "builder.couldNotLoadItems"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (shopifyTarget) {
      setAppliedTemplateId(shopifyTarget.templateId ?? "");
      setName(shopifyTarget.title ?? "");
    }
    if (orderTarget) {
      setName(orderTarget.order.bouquetName ?? orderTarget.order.code);
      setNotes(orderTarget.order.bouquetNotes ?? "");
    }
    void loadCatalog();
  }, [shopifyTarget, orderTarget, loadCatalog]);


  useEffect(() => {
    if (!orderTarget || loading || orderSeeded) return;
    const requested = orderTarget.order.lines.filter(
      (line) => line.role === "BOUQUET_COMPONENT" && line.itemId,
    );
    const seededItems: ItemLine[] = [];
    const seededPaper: PaperLine[] = [];
    for (const line of requested) {
      const quantity = Number(line.quantity);
      const item = items.find((candidate) => candidate.itemId === line.itemId);
      if (item) {
        seededItems.push({
          itemId: item.itemId,
          name: item.name,
          uom: item.defaultUom,
          qty: quantity,
          unitCost: amountOf(item.fifoUnitCostEur),
          // The price the card shows (typed, else worked out from cost), not
          // the typed field alone — an item priced automatically is not
          // "price missing" (G-45; D-51 made an empty price the normal case).
          unitSalePrice:
            item.saleUnitPriceEur != null ? Number(item.saleUnitPriceEur) : null,
          categoryCode: item.categoryCode,
        });
        continue;
      }
      const matchingRolls = rolls.filter((roll) => roll.item.id === line.itemId);
      const roll =
        matchingRolls.find((candidate) => Number(candidate.remainingQty) >= quantity) ??
        matchingRolls[0];
      if (roll) {
        seededPaper.push({
          uid: `pl_${paperUid++}`,
          itemId: roll.item.id,
          batchId: roll.batchId,
          name: roll.item.name,
          lengthLabel: translate("builder.lengthRequested", { cm: quantity }),
          customCm: quantity,
        });
      }
    }
    setItemLines(seededItems);
    setPaperLines(seededPaper);
    setOrderSeeded(true);
  }, [orderTarget, loading, orderSeeded, items, rolls]);

  /**
   * The manager's requested components (G-15 COMPOSED mode), or null for a
   * described bouquet. Owner 2026-10-16 (ORD-2026-00011): these are a
   * suggestion, not a lock — the catalog stays whole, so a florist can swap
   * a stem the shop lacks, and a different build is confirmed at save.
   */
  const requestedItemIds = useMemo(() => {
    if (!orderTarget) return null;
    const ids = orderTarget.order.lines
      .filter((line) => line.role === "BOUQUET_COMPONENT" && line.itemId)
      .map((line) => line.itemId as string);
    return ids.length > 0 ? new Set(ids) : null;
  }, [orderTarget]);

  const rollItemIds = useMemo(() => new Set(rolls.map((roll) => roll.item.id)), [rolls]);
  const unavailableRequested = useMemo(() => {
    if (!orderTarget || !requestedItemIds) return [];
    const available = new Set([...items.map((item) => item.itemId), ...rollItemIds]);
    return orderTarget.order.lines.filter(
      (line) =>
        line.role === "BOUQUET_COMPONENT" &&
        line.itemId &&
        !available.has(line.itemId),
    );
  }, [orderTarget, requestedItemIds, items, rollItemIds]);
  const unavailableRequestedIds = useMemo(
    () => new Set(unavailableRequested.map((line) => line.itemId as string)),
    [unavailableRequested],
  );
  const includedItemIds = useMemo(
    () => new Set([...itemLines.map((l) => l.itemId), ...paperLines.map((p) => p.itemId)]),
    [itemLines, paperLines],
  );

  // Once catalog items load, enrich template-seeded lines with real unit cost /
  // category that the lightweight template payload doesn't carry.
  useEffect(() => {
    if (!items.length) return;
    setItemLines((prev) =>
      prev.map((l) => {
        const match = items.find((i) => i.itemId === l.itemId);
        if (!match) return l;
        return {
          ...l,
          name: match.name,
          uom: match.defaultUom,
          unitCost: amountOf(match.fifoUnitCostEur),
          // The price the card shows (typed, else worked out from cost), not
          // the typed field alone — an item priced automatically is not
          // "price missing" (G-45; D-51 made an empty price the normal case).
          unitSalePrice:
            match.saleUnitPriceEur != null ? Number(match.saleUnitPriceEur) : null,
          categoryCode: match.categoryCode,
        };
      }),
    );
  }, [items]);

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const tabs = useMemo(() => {
    const map = new Map<string, { code: string; label: string; count: number; source: "items" | "rolls" }>();
    for (const it of items) {
      const tab = map.get(it.categoryCode);
      if (tab) tab.count += 1;
      else map.set(it.categoryCode, { code: it.categoryCode, label: it.categoryLabel, count: 1, source: "items" });
    }
    // Paper / ribbon tabs come from active rolls, split by the roll item's category.
    for (const r of rolls) {
      const code = r.item.category?.code ?? "PAPER";
      const label = r.item.category?.label ?? t("common.paper");
      const tab = map.get(code);
      if (tab) tab.count += 1;
      else map.set(code, { code, label, count: 1, source: "rolls" });
    }
    return [...map.values()]
      .filter((tab) => BUILDER_TABS.includes(tab.code))
      .sort((a, b) => BUILDER_TABS.indexOf(a.code) - BUILDER_TABS.indexOf(b.code));
    // `t` IS a dependency: this memo builds label text, so it has to run again
    // when the language changes. Safe here because nothing in it fetches —
    // the house rule that keeps `t` out of a dependency list is about
    // fetch-guarding effects, not about pure computation.
  }, [items, rolls, t]);

  // Keep the active tab valid as data arrives.
  useEffect(() => {
    if (tabs.length === 0) return;
    if (!tabs.some((tab) => tab.code === activeTab)) setActiveTab(tabs[0].code);
  }, [tabs, activeTab]);

  const activeTabMeta = tabs.find((tab) => tab.code === activeTab) ?? null;
  const tabIsRolls = activeTabMeta?.source === "rolls";

  // ── Filtered cards for the active tab ────────────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (it.categoryCode !== activeTab) return false;
      if (colorId && it.color?.id !== colorId) return false;
      // Subcategory chips are flower-only; the guard only bites on FLOWER.
      if (activeTab === "FLOWER" && subCategoryId && it.subCategory?.id !== subCategoryId)
        return false;
      if (q && !it.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, activeTab, colorId, subCategoryId, search]);

  // Subcategory chips show only on the FLOWER tab.
  const showSubcategoryChips = activeTab === "FLOWER" && subcategories.length > 0;

  const visibleRolls = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rolls.filter((r) => {
      const code = r.item.category?.code ?? "PAPER";
      if (code !== activeTab) return false;
      if (colorId && r.item.color?.id !== colorId) return false;
      if (q && !r.item.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rolls, activeTab, colorId, search]);

  // ── Line mutations ───────────────────────────────────────────────────────────
  const addItem = useCallback((it: BouquetBuilderItem) => {
    setResult(null);
    setAppliedTemplateId("");
    setItemLines((prev) => {
      const existing = prev.find((l) => l.itemId === it.itemId);
      if (existing) {
        return prev.map((l) => (l.itemId === it.itemId ? { ...l, qty: l.qty + 1 } : l));
      }
      return [
        ...prev,
        {
          itemId: it.itemId,
          name: it.name,
          uom: it.defaultUom,
          qty: 1,
          unitCost: amountOf(it.fifoUnitCostEur),
          // The price the card shows (typed, else worked out from cost), not
          // the typed field alone — an item priced automatically is not
          // "price missing" (G-45; D-51 made an empty price the normal case).
          unitSalePrice:
            it.saleUnitPriceEur != null ? Number(it.saleUnitPriceEur) : null,
          categoryCode: it.categoryCode,
        },
      ];
    });
  }, []);

  const bumpItem = useCallback((itemId: string, delta: number) => {
    setItemLines((prev) =>
      prev
        .map((l) => (l.itemId === itemId ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0),
    );
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItemLines((prev) => prev.filter((l) => l.itemId !== itemId));
  }, []);

  const removePaper = useCallback((uid: string) => {
    setPaperLines((prev) => prev.filter((p) => p.uid !== uid));
  }, []);

  const confirmRoll = useCallback(
    (choice: { preset?: ConsumablePreset; cm?: number }) => {
      if (!pendingRoll) return;
      const r = pendingRoll;
      const presetMeta = choice.preset ? PRESETS.find((p) => p.value === choice.preset) : undefined;
      /**
       * G-85: a line label is TEXT ON SCREEN, so it is built in the kiosk's
       * language at the moment the florist picks the length — and through the
       * preset's key, not the constant's English `label`.
       */
      const lengthLabel = presetMeta
        ? translate("builder.lengthPreset", {
            size: translate(presetMeta.labelKey),
            cm: presetMeta.cm,
          })
        : translate("builder.lengthCustom", { cm: choice.cm ?? 0 });
      setPaperLines((prev) => [
        ...prev,
        {
          uid: `pl_${paperUid++}`,
          itemId: r.item.id,
          batchId: r.batchId,
          name: r.item.name,
          lengthLabel,
          sizePreset: choice.preset,
          customCm: choice.cm,
        },
      ]);
      setPendingRoll(null);
      setResult(null);
    },
    [pendingRoll],
  );

  /**
   * G-175 (KI-155): what the draft's stems cost, asked of the server (the one
   * FIFO), a moment after the draft stops changing. A line is read from the
   * answer only when the answer is for that very item and quantity, so a
   * figure for an older draft never stands for the current one: until the
   * new answer comes the line and the price read "—" (not costed), never a
   * stale price.
   */
  const draftKey = useMemo(() => itemLines.map((l) => `${l.itemId}:${l.qty}`).join("|"), [itemLines]);
  const [stemCosts, setStemCosts] = useState<Array<StemCostEstimate | undefined> | null>(null);
  useEffect(() => {
    if (draftKey === "") {
      setStemCosts(null);
      return;
    }
    const draft = draftKey.split("|").map((part) => {
      const at = part.lastIndexOf(":");
      return { itemId: part.slice(0, at), quantity: Number(part.slice(at + 1)) };
    });
    let cancelled = false;
    const timer = window.setTimeout(() => {
      shopModeApi
        .floorEstimateStemCosts(draft)
        .then((answer) => {
          if (cancelled) return;
          const byItem = new Map(answer.map((a) => [a.itemId, a]));
          setStemCosts(draft.map((d) => byItem.get(d.itemId)));
        })
        .catch(() => {
          // No answer: every line reads as not costed ("—"), never €0.
          if (!cancelled) setStemCosts(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draftKey]);
  const stemCostOf = useCallback(
    (itemId: string, qty: number) =>
      stemCosts?.find((l) => l?.itemId === itemId && Number(l.quantity) === qty),
    [stemCosts],
  );

  // ── Totals ───────────────────────────────────────────────────────────────────
  const pieces = useMemo(() => itemLines.reduce((sum, l) => sum + l.qty, 0), [itemLines]);
  const lineCount = itemLines.length + paperLines.length;
  /**
   * The one price the build panel shows (owner ruling 2026-09-10).
   *
   * An ESTIMATE: the stems are costed by the server exactly as saving takes
   * them — FIFO across lots (G-175, KI-155; `stemCosts` above) — and paper
   * cost is only known at save. The authoritative numbers come back when the
   * build is saved, and a below-floor SALE is judged server-side against those
   * — never against this. G-45's floor is no longer DISPLAYED here, but it is
   * still computed and enforced where it matters, on the sale.
   *
   * WO-B (OD-198): no price is worked out while any line's cost is unknown —
   * the panel then shows the known cost and how many lines have none.
   */
  const estimate = useMemo(
    () =>
      builderCostEstimate(
        itemLines.map((l) => stemCostOf(l.itemId, l.qty)),
        bouquetPricingRule,
      ),
    [stemCostOf, itemLines, bouquetPricingRule],
  );

  const addPhotos = useCallback(
    async (files: FileList | null, buildId: string) => {
      if (!files || files.length === 0) return;
      setPhotoBusy(true);
      setPhotoError(null);
      try {
        for (const file of Array.from(files)) {
          await shopModeApi.floorAddBouquetPhoto(buildId, file);
        }
        setPhotos(await shopModeApi.floorListBouquetPhotos(buildId));
      } catch (err: unknown) {
        // A failed photo must not read as a failed bouquet — the build is
        // already saved by the time this control exists.
        setPhotoError(
          errorText(err, "builder.couldNotAttachPhoto"),
        );
      } finally {
        setPhotoBusy(false);
      }
    },
    [],
  );

  // ── Save ─────────────────────────────────────────────────────────────────────
  const canSave =
    (itemLines.length > 0 || paperLines.length > 0 || !!appliedTemplateId) &&
    !saving;

  /** "These requested components are not included — proceed?" Resolves with the answer. */
  const [recipeAsk, setRecipeAsk] = useState<{
    change: RecipeChange;
    resolve: (proceed: boolean) => void;
  } | null>(null);
  const askRecipeChange = useCallback(
    (change: RecipeChange) =>
      new Promise<boolean>((resolve) => setRecipeAsk({ change, resolve })),
    [],
  );
  const answerRecipe = useCallback(
    (proceed: boolean) => {
      recipeAsk?.resolve(proceed);
      setRecipeAsk(null);
    },
    [recipeAsk],
  );

  /**
   * The panel scrolls, and the save confirmation renders between the totals
   * and the Save button — i.e. below the fold on a tablet. The florist had to
   * hunt for it, and for "Sell this bouquet" with it (owner report
   * 2026-08-01). Bring it to them instead.
   */
  const resultRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [result]);

  const handleSave = useCallback(async () => {
    if (itemLines.length === 0 && paperLines.length === 0 && !appliedTemplateId) {
      setSaveError(translate("builder.addSomethingFirst"));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        ...(appliedTemplateId
          ? { templateId: appliedTemplateId }
          : itemLines.length > 0
            ? { lines: itemLines.map((l) => ({ itemId: l.itemId, quantity: l.qty, uom: l.uom })) }
            : {}),
        paperUsages: paperLines.map((p) =>
          p.sizePreset
            ? { itemId: p.itemId, batchId: p.batchId, sizePreset: p.sizePreset }
            : { itemId: p.itemId, batchId: p.batchId, customCm: p.customCm as number },
        ),
        name: name.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      type BuildOutcome = {
        buildId: string | null;
        totalCogsEur: string | null;
        unknownCostComponentCount: number | null;
        suggestedSalePriceEur: string | null;
        name: string | null;
      };
      const send = (acceptRecipeChanges: boolean) =>
        boxPrompt.run<BuildOutcome>((boxAnswers): Promise<BuildOutcome> => {
        const body = boxAnswers.length ? { ...payload, boxAnswers } : payload;
        return orderTarget
          ? shopModeApi.floorCompleteCustomerOrderBouquet(
              orderTarget.order.id,
              acceptRecipeChanges ? { ...body, acceptRecipeChanges } : body,
            )
          : shopifyTarget
            ? shopModeApi
                .floorBuildShopifyOrderLine(shopifyTarget.orderId, shopifyTarget.lineId, body)
                .then(() => ({
                  buildId: null,
                  totalCogsEur: null,
                  unknownCostComponentCount: null,
                  suggestedSalePriceEur: null,
                  name: name.trim() || shopifyTarget.title || null,
                }))
            : shopModeApi.floorBuildBouquetComplete(body);
        });
      let res: BuildOutcome;
      try {
        res = await send(false);
      } catch (err: unknown) {
        // The server checks the recipe before it allocates any stock, so no
        // box was asked about yet and nothing is lost by sending again.
        const change = parseRecipeChange(err);
        if (!change) throw err;
        if (!(await askRecipeChange(change))) return;
        res = await send(true);
      }
      setResult({
        cogs: res.totalCogsEur,
        unknownCostComponentCount: res.unknownCostComponentCount,
        suggestedSalePriceEur: res.suggestedSalePriceEur,
        name: res.name,
        buildId: res.buildId,
      });
      shopifyTarget?.onBuilt?.();
      orderTarget?.onBuilt?.();
      // Reset the working bouquet; keep the catalog loaded for the next build.
      setItemLines([]);
      setPaperLines([]);
      setAppliedTemplateId("");
      setName("");
      setNotes("");
      setSellLines([]);
    } catch (err: unknown) {
      setSaveError(errorText(err, "builder.couldNotSave"));
    } finally {
      setSaving(false);
    }
  }, [appliedTemplateId, itemLines, paperLines, name, notes, shopifyTarget, orderTarget, boxPrompt, askRecipeChange]);


  // ── Render: builder ──────────────────────────────────────────────────────────
  return (
    <div className="sf-bouquet">
      {boxPrompt.element}
      <header className="sf-bouquet-topbar">
        <button
          type="button"
          className="button plain shop-floor-back-btn sf-bouquet-back-btn"
          onClick={onExit}
        >
          <ShopFloorIcon name="arrow-left" size={20} />
          {t("builder.back")}
        </button>
        {/* The topbar search filters the FLOWER catalog; in checkout mode
            that catalog is off-screen and the finder brings its own search,
            so showing this one would be a control that does nothing. */}
        {!checkoutMode ? (
          <label className="sf-bouquet-search">
            <ShopFloorIcon name="search" size={21} />
            <input
              type="search"
              placeholder={t("waste.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("builder.searchItems")}
            />
          </label>
        ) : null}
      </header>

      {orderTarget ? (
        <ShopFloorOrderBrief
          order={orderTarget.order}
          includedItemIds={includedItemIds}
          unavailableItemIds={unavailableRequestedIds}
          cmItemIds={rollItemIds}
        />
      ) : null}

      {recipeAsk ? (
        <div className="sf-bouquet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="sf-bouquet-modal">
            <h4 className="sf-bouquet-modal-title">{t("builder.recipeConfirmTitle")}</h4>
            {recipeAsk.change.missing.length > 0 ? (
              <p>
                {t("builder.recipeMissing", {
                  items: recipeAsk.change.missing.map((i) => i.name).join(", "),
                })}
              </p>
            ) : null}
            {recipeAsk.change.extra.length > 0 ? (
              <p>
                {t("builder.recipeExtra", {
                  items: recipeAsk.change.extra.map((i) => i.name).join(", "),
                })}
              </p>
            ) : null}
            <p className="muted">{t("builder.recipeManagerTold")}</p>
            <div className="shop-floor-action-stack">
              <button
                type="button"
                className="button shop-floor-action-btn"
                onClick={() => answerRecipe(true)}
              >
                {t("builder.recipeProceed")}
              </button>
              <button type="button" className="button plain" onClick={() => answerRecipe(false)}>
                {t("builder.recipeGoBack")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Deliberately NOT inside the brief overlay: the florist must know
          before building that a requested stem has to be swapped. */}
      {orderTarget && unavailableRequested.length > 0 ? (
        <div className="alert error sf-order-strip-alert">
          {t("builder.unavailableRequested", {
            items: unavailableRequested
              .map((line) => line.itemName ?? line.description)
              .join(", "),
          })}
        </div>
      ) : null}


      <div className="sf-bouquet-body">
        {checkoutMode ? (
          /* Left, checkout mode: the finished build freed this canvas, so the
             sale's item picker gets the wide columns the panel could never
             give it (owner screenshots 2026-08-09: one cramped card column). */
          <div className="sf-bouquet-catalog sf-checkout-catalog">
            <div className="sf-checkout-head">
              <h3>
                {orderTarget
                  ? t("builder.loggedForOrder")
                  : t("builder.addShopItems")}
              </h3>
              {orderTarget ? (
                <button type="button" className="button plain" onClick={onExit}>
                  {t("resolver.done")}
                </button>
              ) : (
                <button type="button" className="button plain" onClick={exitCheckout}>
                  {t("builder.startNext")}
                </button>
              )}
            </div>
            {orderTarget ? (
              /* OD-31: no finder here — items picked would feed a walk-in
                 ticket this build must never have. */
              <p className="muted">{t("builder.orderNoCounterSale")}</p>
            ) : (
              <>
                {sellTicket.stockError ? (
                  <div className="alert error">{sellTicket.stockError}</div>
                ) : null}
                <StockItemFinder
                  items={sellTicket.stock}
                  loading={sellTicket.stockLoading}
                  qtyInTicket={sellTicket.qtyInTicket}
                  onPick={sellTicket.pickItem}
                />
              </>
            )}
          </div>
        ) : (
        /* Left: catalog */
        <div className="sf-bouquet-catalog">
        {/* Filters live INSIDE the catalog column so they run from the left
            edge only as far as the bouquet panel, leaving that panel the full
            height of the device (owner design call 2026-08-01). */}
        <div className="sf-bouquet-filter-panel">
          {/* Row 1 — Category */}
          <div className="sf-filter-row">
            <span className="sf-filter-label">{t("waste.filterCat")}</span>
            <div className="sf-filter-chips" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.code}
                  type="button"
                  role="tab"
                  aria-selected={tab.code === activeTab}
                  className={`sf-tab-chip${tab.code === activeTab ? " sf-tab-chip--active" : ""}`}
                  onClick={() => setActiveTab(tab.code)}
                >
                  {categoryLabel(tab.code, tab.label)}
                </button>
              ))}
            </div>
          </div>
          {/* Row 2 — Color */}
          <div className="sf-filter-row">
            <span className="sf-filter-label">{t("waste.filterColor")}</span>
            <div className="sf-filter-chips" role="group" aria-label={t("builder.filterByColor")}>
              <button
                type="button"
                aria-pressed={colorId === ""}
                className={`sf-color-chip${colorId === "" ? " sf-color-chip--active" : ""}`}
                onClick={() => setColorId("")}
              >
                {t("common.all")}
              </button>
              {colors.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={colorId === c.id}
                  className={`sf-color-chip${colorId === c.id ? " sf-color-chip--active" : ""}`}
                  onClick={() => setColorId(c.id)}
                >
                  <span className="sf-color-dot" style={{ background: c.hex ?? "#bbb" }} aria-hidden="true" />
                  {colourLabel(c.name)}
                </button>
              ))}
            </div>
          </div>
          {/* Row 3 — Type (FLOWER tab only) */}
          {showSubcategoryChips ? (
            <div className="sf-filter-row">
              <span className="sf-filter-label">{t("waste.filterType")}</span>
              <div className="sf-filter-chips" role="group" aria-label={t("builder.filterByType")}>
                <button
                  type="button"
                  aria-pressed={subCategoryId === ""}
                  className={`sf-type-chip${subCategoryId === "" ? " sf-type-chip--active" : ""}`}
                  onClick={() => setSubCategoryId("")}
                >
                  {t("common.all")}
                </button>
                {subcategories.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={subCategoryId === s.id}
                    className={`sf-type-chip${subCategoryId === s.id ? " sf-type-chip--active" : ""}`}
                    onClick={() => setSubCategoryId(s.id)}
                  >
                    {flowerTypeLabel(s.code, s.label)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

          {loadError ? <div className="alert error">{loadError}</div> : null}

          {loading ? (
            <div className="empty-state">{t("builder.loadingItems")}</div>
          ) : tabIsRolls ? (
            <RollGrid rolls={visibleRolls} onPick={(r) => setPendingRoll(r)} />
          ) : (
            <ItemGrid items={visibleItems} onAdd={addItem} />
          )}
        </div>
        )}

        {/* Right: this bouquet. In checkout mode the build-time controls
            (name, description, stems, totals) step aside — the panel is the
            TICKET now, and the just-reset empty fields would only bury the
            money under "Nothing added yet" and four zero rows. */}
        <aside className="sf-bouquet-panel">
          {!checkoutMode ? (
          <>
          <label className="sf-bouquet-field">
            <span>{t("builder.nameLabel")}</span>
            <input
              type="text"
              placeholder={t("builder.namePlaceholder")}
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          {/* G-28: this was a one-line input stranded below the totals, which
              showed ~40 of its 500 characters and told the florist to keep it
              short. It is what G-16's recipe view renders, so a thin note here
              is a thin record forever. Sits under the name now, so the panel
              reads: what is it → what is in it → what it costs → save. */}
          <label className="sf-bouquet-field">
            <span>{t("builder.describeLabel")}</span>
            <textarea
              rows={3}
              placeholder={t("builder.describePlaceholder")}
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="sf-bouquet-lines">
            {itemLines.length === 0 && paperLines.length === 0 ? (
              <div className="sf-bouquet-empty">
                <ShopFloorIcon name="bouquets" size={32} />
                <span>{t("builder.nothingAdded")}</span>
              </div>
            ) : (
              <>
                {itemLines.map((l) => (
                  <div key={l.itemId} className="sf-bouquet-line">
                    <div className="sf-bouquet-line-main">
                      <span className="sf-bouquet-line-name">{l.name}</span>
                      <span className="sf-bouquet-line-sub muted">
                        <UnitCost value={l.unitCost} uom={l.uom} />
                      </span>
                    </div>
                    <div className="sf-bouquet-stepper">
                      <button type="button" onClick={() => bumpItem(l.itemId, -1)} aria-label={t("builder.less", { name: l.name })}>
                        −
                      </button>
                      <span className="sf-bouquet-stepper-qty">{l.qty}</span>
                      <button type="button" onClick={() => bumpItem(l.itemId, 1)} aria-label={t("builder.more", { name: l.name })}>
                        +
                      </button>
                    </div>
                    <span className="sf-bouquet-line-cost">
                      {stemLineCostText(stemCostOf(l.itemId, l.qty))}
                    </span>
                    <button
                      type="button"
                      className="sf-bouquet-line-remove"
                      onClick={() => removeItem(l.itemId)}
                      aria-label={t("builder.removeLine", { name: l.name })}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {paperLines.map((p) => (
                  <div key={p.uid} className="sf-bouquet-line">
                    <div className="sf-bouquet-line-main">
                      <span className="sf-bouquet-line-name">{p.name}</span>
                      <span className="sf-bouquet-line-sub muted">{p.lengthLabel}</span>
                    </div>
                    <span className="sf-bouquet-line-cost muted">{t("builder.atSave")}</span>
                    <button
                      type="button"
                      className="sf-bouquet-line-remove"
                      onClick={() => removePaper(p.uid)}
                      aria-label={t("builder.removeLine", { name: p.name })}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="sf-bouquet-totals">
            <span className="muted">
              {t("builder.pieces", { count: pieces })} ·{" "}
              {t("builder.lineCount", { count: lineCount })}
            </span>
            {/*
              Owner ruling 2026-09-10: the build panel shows ONE number — the
              price to ask. "Material cost", "Suggested price" and G-45's
              "Never below" floor were four figures competing for a glance on a
              960px kiosk, and the florist only acts on one of them.

              This hides them; it does not weaken them. G-45's floor is still
              computed and enforced SERVER-side on every sale, the till still
              warns before a below-floor price is charged, and the kiosk still
              asks the florist to justify one afterwards. Cost and margin
              remain on the owner's side of the app.
            */}
            {/*
              Review fix: this used to render nothing at all when the price
              could not be worked out — a florist choosing stems saw a piece
              count and no money, with nothing saying why. It happens for real:
              no category with code FLOWER, or a target margin outside 0-100.
              The house rule is the honest unknown over silence, so say "—" and
              name the reason.

              WO-B part 7 (OD-198): the other reason there is no price is a
              line whose cost is unknown — a price from the known part would be
              too low. Then, and only then, the panel shows a second figure:
              the known cost, with how many lines have none.
            */}
            <div className="sf-bouquet-total-amount">
              <span>
                {estimate.price.kind === "price" && paperLines.length > 0
                  ? t("builder.priceEstimated")
                  : t("builder.price")}
              </span>
              <strong>
                {estimate.price.kind === "price" ? eur(estimate.price.eur) : NO_AMOUNT}
              </strong>
            </div>
            {estimate.price.kind === "costUnknown" ? (
              <>
                <div className="sf-bouquet-known-cost">
                  <span>{t("builder.knownCost")}</span>
                  <strong>
                    {estimate.knownCostEur != null ? eur(estimate.knownCostEur) : NO_AMOUNT}
                  </strong>
                </div>
                <span className="badge warning sf-bouquet-cost-pill">
                  {unknownCountPill(estimate.unknownCostLines)}
                </span>
              </>
            ) : null}
            {estimate.price.kind === "noRule" ? (
              <span className="muted sf-bouquet-price-unknown">
                {t("builder.noPriceYet")}
              </span>
            ) : null}
          </div>
          </>
          ) : null}

          {saveError ? <div className="alert error">{saveError}</div> : null}
          {result ? (
            <div className="sf-bouquet-result" ref={resultRef} role="status">
              {/* 2026-10-13 (owner: the green box "looks extremely ugly"): a
                  plain section in the panel, styled like the till's ticket. */}
              <div className="sf-bouquet-result-head">
                <span className="sf-bouquet-result-check" aria-hidden="true">
                  <ShopFloorIcon name="check" size={16} />
                </span>
                <strong className="sf-bouquet-result-title">
                  {result.name
                    ? t("builder.loggedNamed", { name: result.name })
                    : t("builder.logged")}
                </strong>
              </div>
              <div className="sf-bouquet-result-figs">
                <div>
                  <span>
                    {resultCost.partial ? t("builder.knownCost") : t("builder.costToBuild")}
                  </span>
                  <strong>{resultCost.amount}</strong>
                  {resultCost.pill ? (
                    <span className="badge warning sf-bouquet-result-pill">
                      {resultCost.pill}
                    </span>
                  ) : null}
                </div>
                <div>
                  <span>{t("builder.suggestedPrice")}</span>
                  <strong>
                    {result.suggestedSalePriceEur != null
                      ? eur(Number(result.suggestedSalePriceEur))
                      : "—"}
                  </strong>
                </div>
              </div>
              {result.buildId ? (
                <div className="sf-bouquet-photos">
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => {
                      void addPhotos(e.target.files, result.buildId as string);
                      e.target.value = "";
                    }}
                  />
                  <input
                    ref={libraryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                      void addPhotos(e.target.files, result.buildId as string);
                      e.target.value = "";
                    }}
                  />
                  <div className="sf-bouquet-photo-actions">
                    <button
                      type="button"
                      className="button tinted"
                      disabled={photoBusy}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      {t("common.takePhoto")}
                    </button>
                    <button
                      type="button"
                      className="button tinted"
                      disabled={photoBusy}
                      onClick={() => libraryInputRef.current?.click()}
                    >
                      {t("builder.choosePhoto")}
                    </button>
                    {photos.length > 0 ? (
                      <span className="muted">
                        {t("builder.photosAttached", { count: photos.length })}
                      </span>
                    ) : null}
                  </div>
                  {photoBusy ? <span className="muted">{t("builder.attaching")}</span> : null}
                  {photoError ? (
                    <span className="sf-bouquet-photo-error">{photoError}</span>
                  ) : null}
                </div>
              ) : null}
              {/* G-46 GAP 2: the walk-in sale completes HERE — price,
                  Cash/Card, optional stock items via the shared finders; the
                  old handoff to the full till is retired (owner decision 1:
                  the common case never changes screen). OD-31: never for an
                  order-linked build — its money lives in the order's ledger. */}
              {result.buildId && !orderTarget ? (
                <ShopFloorInlineSell
                  key={result.buildId}
                  build={{
                    buildId: result.buildId,
                    name: result.name,
                    suggestedSalePriceEur: result.suggestedSalePriceEur,
                  }}
                  lines={sellTicket.lines}
                  onLinesChange={setSellLines}
                  onSold={handleSold}
                />
              ) : null}
            </div>
          ) : null}

          {/* Checkout mode hides the (necessarily disabled) save button — the
              next save starts by leaving checkout, never around the sale. */}
          {!checkoutMode ? (
            <button
              type="button"
              className="button sf-bouquet-save-btn"
              onClick={() => void handleSave()}
              disabled={!canSave}
            >
              {saving ? (
                t("belowFloor.saving")
              ) : (
                <>
                  <ShopFloorIcon name="check" size={21} />
                  {t("builder.saveLog")}
                </>
              )}
            </button>
          ) : null}
        </aside>
      </div>

      {/* Roll size prompt */}
      {pendingRoll ? (
        <div className="sf-bouquet-modal-backdrop" role="dialog" aria-modal="true" aria-label={t("builder.chooseLength")}>
          <div className="sf-bouquet-modal">
            <h4 className="sf-bouquet-modal-title">{pendingRoll.item.name}</h4>
            <p className="muted" style={{ margin: 0 }}>
              {t("builder.rollRemaining", {
                cm: pendingRoll.remainingQty,
                zone: pendingRoll.zoneName,
              })}
            </p>
            <div className="sf-bouquet-size-row">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className="button tinted sf-bouquet-size-btn"
                  onClick={() => confirmRoll({ preset: p.value })}
                >
                  {t(p.labelKey)}
                  <small>{t("builder.presetCm", { cm: p.cm })}</small>
                </button>
              ))}
            </div>
            <div className="sf-bouquet-custom-row">
              <input
                type="number"
                min={0.1}
                step="0.1"
                value={customCm}
                onChange={(e) => setCustomCm(e.target.value)}
                aria-label={t("builder.customLengthLabel")}
              />
              <button
                type="button"
                className="button"
                onClick={() => {
                  const n = Number(customCm);
                  if (Number.isFinite(n) && n > 0) confirmRoll({ cm: n });
                }}
              >
                {t("builder.customCm")}
              </button>
            </div>
            <button type="button" className="button plain" onClick={() => setPendingRoll(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Card grids ─────────────────────────────────────────────────────────────────

function ItemGrid({
  items,
  onAdd,
}: {
  items: BouquetBuilderItem[];
  onAdd(it: BouquetBuilderItem): void;
}) {
  const { t } = useT();
  if (items.length === 0) {
    return <div className="empty-state">{t("builder.noFilterMatch")}</div>;
  }
  return (
    <div className="sf-bouquet-grid">
      {items.map((it) => {
        const qty = Number(it.qtyOnHand);
        const threshold = it.reorderThreshold != null ? Number(it.reorderThreshold) : null;
        const low = threshold != null && Number.isFinite(qty) && qty <= threshold;
        return (
          <button
            key={it.itemId}
            type="button"
            className="sf-bouquet-card"
            onClick={() => onAdd(it)}
            aria-label={t("builder.addToBouquet", { name: it.name })}
          >
            <div className="sf-bouquet-card-photo">
              {it.color ? (
                <span
                  className="sf-bouquet-card-dot"
                  style={{ background: it.color.hex ?? "#bbb" }}
                  title={colourLabel(it.color.name)}
                  aria-hidden="true"
                />
              ) : null}
              <ShopFloorBouquetThumb id={it.itemId} imageKey={it.imageKey} alt={it.name} />
            </div>
            <div className="sf-bouquet-card-body">
              <span className="sf-bouquet-card-name">{it.name}</span>
              <div className="sf-bouquet-card-meta">
                <span className="sf-bouquet-card-price">
                  <UnitCost value={it.fifoUnitCostEur} uom={it.defaultUom} />
                </span>
                {it.color ? <span aria-hidden="true">·</span> : null}
                {it.color ? (
                  <span className="sf-bouquet-card-color muted">
                    {colourLabel(it.color.name)}
                  </span>
                ) : null}
              </div>
              <span className="sf-bouquet-card-sale-price">
                {it.saleUnitPriceEur != null
                  ? t(
                      it.saleUnitPriceIsDerived
                        ? "builder.sellingPriceDerived"
                        : "builder.sellingPrice",
                      {
                        price: eur(Number(it.saleUnitPriceEur)),
                        unit: uomLabel(it.defaultUom, 1),
                      },
                    )
                  : t("finder.noPriceYet")}
              </span>
              <span className={`sf-bouquet-card-stock${low ? " sf-bouquet-card-stock--low" : ""}`}>
                {low ? `${t("builder.low")} · ` : ""}
                {t("builder.qtyInStock", { qty: it.qtyOnHand })}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function RollGrid({
  rolls,
  onPick,
}: {
  rolls: PaperRollRow[];
  onPick(r: PaperRollRow): void;
}) {
  const { t } = useT();
  if (rolls.length === 0) {
    return <div className="empty-state">{t("builder.noRolls")}</div>;
  }
  return (
    <div className="sf-bouquet-grid">
      {rolls.map((r) => (
        <button
          key={r.batchId}
          type="button"
          className="sf-bouquet-card"
          onClick={() => onPick(r)}
          aria-label={t("builder.addToBouquet", { name: r.item.name })}
        >
          <div className="sf-bouquet-card-photo">
            {r.item.color ? (
              <span
                className="sf-bouquet-card-dot"
                style={{ background: r.item.color.hex ?? "#bbb" }}
                title={colourLabel(r.item.color.name)}
                aria-hidden="true"
              />
            ) : null}
            <ShopFloorBouquetThumb id={r.item.id} imageKey={null} alt={r.item.name} />
          </div>
          <div className="sf-bouquet-card-body">
            <span className="sf-bouquet-card-name">{r.item.name}</span>
            <div className="sf-bouquet-card-meta">
              <span className="sf-bouquet-card-price muted">{r.batchCode}</span>
              {r.item.color ? (
                <span className="sf-bouquet-card-color muted">
                  {colourLabel(r.item.color.name)}
                </span>
              ) : null}
            </div>
            <span className="sf-bouquet-card-stock">
              {t("builder.rollStock", { cm: r.remainingQty, zone: r.zoneName })}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * WO-B (OD-198): a stem's next cost per unit on a card or a line — "Cost
 * unknown" when the lot it would come from has none, never €0; "—" while the
 * feed has not costed the item. A per-unit cost keeps up to four decimals
 * (`cost`), so a fraction of a cent never reads as €0.00.
 */
function UnitCost({
  value,
  uom,
}: {
  value: string | number | null | undefined;
  uom: DefaultUom;
}) {
  const { t } = useT();
  const figure = unitCostFigure(value);
  return (
    <>
      {figure.amount != null
        ? t("builder.costPerUnit", { price: figure.amount, unit: uomLabel(uom, 1) })
        : (figure.pill ?? NO_AMOUNT)}
    </>
  );
}
